import { useEffect, useId, useMemo, useRef, useState } from 'react';
import './Combobox.css';

const MAX_SHOWN = 200;

/**
 * Type-to-search single choice, used instead of long dropdowns.
 *
 * options: [{ value, label, sub?, search? }]  (sub = grey text on the right)
 * value: the chosen value ('' = nothing)
 * onChange(value, option | null)
 * emptyLabel: when set, a first option meaning "nothing / all" (e.g. "All clients")
 * onCreate(text): when set, a "+ Create …" option at the bottom (createLabel = "client")
 */
function Combobox({
  options,
  value,
  onChange,
  placeholder = 'Type to search…',
  emptyLabel,
  onCreate,
  createLabel = 'new',
  disabled = false,
  required = false,
  id,
  ariaLabel,
  className = '',
}) {
  const autoId = useId();
  const inputId = id || `cb-${autoId}`;
  const listId = `${inputId}-list`;
  const boxRef = useRef(null);
  const listRef = useRef(null);
  const selected = useMemo(() => options.find((o) => String(o.value) === String(value)) || null, [options, value]);
  const [text, setText] = useState(selected?.label || '');
  const [open, setOpen] = useState(false);
  const [typed, setTyped] = useState(false);
  const [active, setActive] = useState(0);

  // Show the chosen label when the value changes from outside.
  useEffect(() => {
    setText(selected?.label || '');
    setTyped(false);
  }, [selected]);

  useEffect(() => {
    const onDown = (event) => {
      if (boxRef.current && !boxRef.current.contains(event.target)) {
        setOpen(false);
        setText(selected?.label || '');
        setTyped(false);
      }
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [selected]);

  const matches = useMemo(() => {
    const term = typed ? text.trim().toLowerCase() : '';
    if (!term) return options.slice(0, MAX_SHOWN);
    const words = term.split(/\s+/);
    return options
      .filter((o) => {
        const hay = `${o.label} ${o.sub || ''} ${o.search || ''}`.toLowerCase();
        return words.every((w) => hay.includes(w));
      })
      .slice(0, MAX_SHOWN);
  }, [options, text, typed]);

  // Items in the open list: [empty], matches..., [create]
  const items = useMemo(() => {
    const list = [];
    if (emptyLabel && !typed) list.push({ kind: 'empty' });
    matches.forEach((o) => list.push({ kind: 'option', option: o }));
    if (onCreate) list.push({ kind: 'create' });
    return list;
  }, [emptyLabel, typed, matches, onCreate]);

  useEffect(() => {
    if (!open) return;
    const index = items.findIndex((item) => item.kind === 'option' && String(item.option.value) === String(value));
    setActive(index >= 0 ? index : 0);
    // only when opening
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    const el = listRef.current?.querySelector(`[data-index="${active}"]`);
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  const pick = (item) => {
    if (!item) return;
    if (item.kind === 'create') {
      setOpen(false);
      const name = typed ? text.trim() : '';
      setText(selected?.label || '');
      setTyped(false);
      onCreate(name);
      return;
    }
    if (item.kind === 'empty') {
      onChange('', null);
      setText('');
    } else {
      onChange(String(item.option.value), item.option);
      setText(item.option.label);
    }
    setTyped(false);
    setOpen(false);
  };

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!open) setOpen(true);
      else setActive((i) => Math.min(i + 1, items.length - 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (event.key === 'Enter') {
      if (open) {
        event.preventDefault();
        pick(items[active]);
      }
    } else if (event.key === 'Escape') {
      if (open) {
        // Close only the list, not a modal around it.
        event.preventDefault();
        event.stopPropagation();
        event.nativeEvent.stopPropagation();
        setOpen(false);
        setText(selected?.label || '');
        setTyped(false);
      }
    } else if (event.key === 'Tab') {
      setOpen(false);
      setText(selected?.label || '');
      setTyped(false);
    }
  };

  const createText = typed && text.trim() ? `+ Create ${createLabel} “${text.trim()}”` : `+ Create ${createLabel}`;

  return (
    <div ref={boxRef} className={`combobox${open ? ' is-open' : ''}${disabled ? ' is-disabled' : ''} ${className}`.trim()}>
      <div className="combobox__field">
        <input
          id={inputId}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-label={ariaLabel}
          aria-activedescendant={open ? `${listId}-${active}` : undefined}
          autoComplete="off"
          value={text}
          placeholder={selected ? '' : emptyLabel && !value ? emptyLabel : placeholder}
          disabled={disabled}
          // Required: the browser blocks the form while nothing is chosen.
          required={required && !value}
          onFocus={(event) => {
            setOpen(true);
            event.target.select();
          }}
          onClick={() => setOpen(true)}
          onChange={(event) => {
            setText(event.target.value);
            setTyped(true);
            setOpen(true);
            setActive(0);
          }}
          onKeyDown={onKeyDown}
        />
        {value && !disabled && !required ? (
          <button
            type="button"
            className="combobox__clear"
            aria-label="Clear"
            tabIndex={-1}
            onMouseDown={(event) => event.preventDefault()}
            onClick={(event) => {
              event.preventDefault();
              onChange('', null);
              setText('');
              setTyped(false);
            }}
          >
            ×
          </button>
        ) : null}
        <span className="combobox__caret" aria-hidden="true" />
      </div>

      {open && !disabled ? (
        <ul id={listId} ref={listRef} className="combobox__list" role="listbox">
          {items.map((item, index) => {
            const isActive = index === active;
            if (item.kind === 'empty') {
              return (
                <li
                  key="__empty"
                  id={`${listId}-${index}`}
                  data-index={index}
                  role="option"
                  aria-selected={!value}
                  className={`combobox__option combobox__option--empty${isActive ? ' is-active' : ''}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActive(index)}
                  onClick={(event) => {
                  // Inside a <label>, a click would also re-focus the input and re-open the list.
                  event.preventDefault();
                  pick(item);
                }}
                >
                  {emptyLabel}
                </li>
              );
            }
            if (item.kind === 'create') return null;
            const o = item.option;
            const chosen = String(o.value) === String(value);
            return (
              <li
                key={o.value}
                id={`${listId}-${index}`}
                data-index={index}
                role="option"
                aria-selected={chosen}
                className={`combobox__option${isActive ? ' is-active' : ''}${chosen ? ' is-chosen' : ''}`}
                onMouseDown={(event) => event.preventDefault()}
                onMouseEnter={() => setActive(index)}
                onClick={(event) => {
                  // Inside a <label>, a click would also re-focus the input and re-open the list.
                  event.preventDefault();
                  pick(item);
                }}
              >
                <strong>{o.label}</strong>
                {o.sub ? <span>{o.sub}</span> : null}
              </li>
            );
          })}
          {!matches.length ? <li className="combobox__none">No match for “{text.trim()}”.</li> : null}
          {matches.length === MAX_SHOWN ? <li className="combobox__none">Showing the first {MAX_SHOWN} — keep typing to narrow.</li> : null}
          {onCreate
            ? (() => {
                const index = items.length - 1;
                const isActive = index === active;
                const item = items[index];
                return (
                <li
                  key="__create"
                  id={`${listId}-${index}`}
                  data-index={index}
                  role="option"
                  aria-selected={false}
                  className={`combobox__option combobox__option--create${isActive ? ' is-active' : ''}`}
                  onMouseDown={(event) => event.preventDefault()}
                  onMouseEnter={() => setActive(index)}
                  onClick={(event) => {
                  // Inside a <label>, a click would also re-focus the input and re-open the list.
                  event.preventDefault();
                  pick(item);
                }}
                >
                  {createText}
                </li>
              );
              })()
            : null}
        </ul>
      ) : null}
    </div>
  );
}

export default Combobox;
